import { FC } from "react";
import { Alert, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import { ImportType } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

interface ImportLegendProps {
  importType: ImportType;
}

const legendTexts = {
  [ImportType.EMPLOYEE_CALENDAR_CONFIGURATION]: {
    title: "Import kalendarzy pracowników",
    subtitle: "Aby zaktualizować kalendarze pracowników:",
    list: [
      "Kliknij ”Eksportuj”, aby pobrać plik z kalendarzami.",
      "W pliku znajdź wiersz pracownika, którego chcesz edytować.",
      "W kolumnie H (”Zmiana”) wpisz nazwę nowego kalendarza.",
      "Zapisz plik jako .xls lub .csv.",
      "Przeciągnij plik do okienka poniżej lub wybierz go z dysku i zapisz zmiany.",
    ],
  },
  [ImportType.EMPLOYEE_ABSENCE_CONFIGURATION]: {
    title: "Import wzorców i wymiarów",
    subtitle: "Aby zaktualizować wzorce i wymiary:",
    list: [
      "Kliknij ”Eksportuj”, aby pobrać plik z wzorcami i wymiarami.",
      "W pliku znajdź wiersz pracownika, którego chcesz edytować.",
      {
        title: "Wzorzec obecności/nieobecności (kolumny G-H)",
        items: [
          "W kolumnie H („Zmiana”) wpisz nową liczbę dni wzorca.",
          "Zmiana zacznie obowiązywać od następnego okresu rozliczeniowego.",
        ],
      },
      {
        title: "Wymiar urlopu (kolumny I-K)",
        items: [
          {
            title: `W kolumnie K („Zmiana") wpisz liczbę dni, które zostaną zsumowane z wymiarem podanym w kolumnie J "Dostępne".`,
            items: [
              "Wpisz liczbę dodatnią - dni zostaną dodane do dostępnego wymiaru.",
              "Wpisz liczbę ujemną - dni zostaną odjęte od dostępnego wymiaru.",
            ],
          },
          // eslint-disable-next-line max-len
          `Aby przypisać zmianę do konkretnego miesiąca, uzupełnij kolumnę I („Okres"). Pamiętaj: możesz podać okres tylko w bieżącym roku. Jeśli zmienisz rok plik zostanie odrzucony.`,
        ],
      },
      "Zapisz plik jako .xls lub .csv.",
      "Przeciągnij plik do okienka poniżej lub wybierz go z dysku i zapisz zmiany.",
    ],
  },
  [ImportType.ABSENCE_REQUESTS]: {
    title: "Import wniosków z innego systemu",
    subtitle: "Aby importować wnioski:",
    list: [
      "Utwórz kalendarze dla lat, dla których chcesz zaimportować wnioski.",
      "Kliknij przycisk „Eksportuj”.",
      {
        title: "Uzupełnij wskazane poniżej kolumny:",
        items: [
          "ID pracownika - np. numer kadrowo-płacowy, adres e-mail",
          "Imię",
          "Nazwisko",
          "Rodzaj wniosku - nazwa importowanego szablonu wniosku",
          "Data złożenia / Nieobecność od / Nieobecność do - w formacie RRRR-MM-DD",
          "(dla wniosków godzinowych) Początek / Koniec nieobecności - w formacie HH:MM",
          "Status - uzupełnij 1 z podanych statusów: Zaakceptowany, Oczekuje na akceptację, Wymagana zmiana, Anulowany lub Odrzucony",
          "(opcjonalnie) Akceptujący/anulujący/odrzucający - w formacie IMIĘ NAZWISKO",
          "(opcjonalnie) ID akceptującego/anulującego/odrzucającego",
        ],
      },
      "Zapisz plik jako .xls lub .csv.",
      "Przeciągnij plik do okienka poniżej lub wybierz go z dysku i zapisz zmiany.",
    ],
  },
};

const ImportLegend: FC<ImportLegendProps> = ({ importType }) => {
  const currentImportLegend = legendTexts[importType];

  return (
    <>
      <Typography component="h2" variant="h6">
        {__(currentImportLegend.title)}
      </Typography>
      <Typography variant="subtitle1" mt="1rem">
        {__(currentImportLegend.subtitle)}
      </Typography>
      <Typography
        variant="body2"
        component="ol"
        sx={{
          pl: 2,
          m: "1rem 0",
          "& li": {
            mb: "0.5rem",
          },
          "& .nested-list": {
            pl: 3,
            mt: "0.25rem",
            listStyle: "disc",
          },
          "& .subnested-list": {
            pl: 3,
            mt: "0.25rem",
            listStyle: "circle",
          },
        }}
      >
        {currentImportLegend.list.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Typography component="li" variant="body1" key={index}>
            {typeof item === "string" ? (
              __(item)
            ) : (
              <>
                <Typography>{__(item.title)}</Typography>
                <ul className="nested-list">
                  {item.items.map((subItem, subIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={subIndex}>
                      {typeof subItem === "string" ? (
                        __(subItem)
                      ) : (
                        <>
                          <Typography>{__(subItem.title)}</Typography>
                          <ul className="subnested-list">
                            {subItem.items.map((subSubItem, subSubIndex) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <li key={subSubIndex}>{__(subSubItem)}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Typography>
        ))}
      </Typography>
      <Alert severity="info" sx={{ mb: "1.5rem" }}>
        {__(
          "Nie edytuj kolumn innych niż wskazane w instrukcji. W przypadku błędnych danych cały plik zostanie odrzucony."
        )}
      </Alert>
    </>
  );
};

export default ImportLegend;
